import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, Paper, Divider, CircularProgress, Alert } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import { gnewsService } from '../../api/services';

const ExternalNews = ({ country = 've', limit = 5 }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await gnewsService.getTrendingNews(country, limit);
        
        // Manejar respuesta paginada o array directo
        let newsData = [];
        if (res.data && Array.isArray(res.data)) {
            newsData = res.data;
        } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
            newsData = res.data.results;
        } else if (Array.isArray(res)) {
            newsData = res;
        }
        
        setNews(newsData);
      } catch (err) {
        console.error('Error fetching external news:', err);
        setError('No se pudieron cargar las noticias externas');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [country, limit]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Alert severity="info">{error}</Alert>
      </Paper>
    );
  }

  if (news.length === 0) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ 
      p: 3, 
      bgcolor: 'background.paper', 
      borderRadius: 4, 
      border: '1px solid',
      borderColor: 'divider'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PublicIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5" fontWeight="800">
          Actualidad Tech Global
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      
      <List>
        {news.map((item, index) => (
          <React.Fragment key={item.id}>
            <ListItem 
              alignItems="flex-start" 
              component="a" 
              href={`/news/${item.id}`}
              sx={{ borderRadius: 2, '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer', textDecoration: 'none' }}
            >
              <ListItemAvatar>
                <Avatar 
                  variant="rounded" 
                  src={item.image || `https://source.unsplash.com/100x100/?tech,${item.id}`} 
                  alt={item.source?.name || 'News'} 
                  sx={{ width: 60, height: 60, mr: 2 }} 
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                    {item.title}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Typography component="span" variant="body2" color="primary" fontWeight="600">
                      {item.source?.name || 'Unknown'}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                      •
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {new Date(item.published_at).toLocaleDateString('es-VE')}
                    </Typography>
                  </Box>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
            {index < news.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <TrendingUpIcon color="action" sx={{ mr: 1, fontSize: 16 }} />
        <Typography variant="caption" color="text.secondary">
          Noticias actualizadas automáticamente
        </Typography>
      </Box>
    </Paper>
  );
};

export default ExternalNews;
